import { useState } from 'react';
import TagChip, { tagWords } from '../TagChip';

/** How many kinds of tag a cell shows before "+N more". */
export const TAG_KINDS_SHOWN = 3;

/** Tags grouped by kind, in the order they first appear. Pure. */
export function groupTags(tags: { dimension: string; value: string }[]): { dimension: string; values: string[] }[] {
  const out: { dimension: string; values: string[] }[] = [];
  for (const t of tags) {
    const row = out.find(g => g.dimension === t.dimension);
    if (row) {
      if (!row.values.includes(t.value)) row.values.push(t.value);
    } else out.push({ dimension: t.dimension, values: [t.value] });
  }
  return out;
}

/**
 * A row's tags in a table cell: one line per kind, the kind named once.
 *
 * A chip per tag, each with its kind in front, stacked up to fifteen chips in
 * one cell and made the table unreadable. Past three kinds the rest wait behind
 * "+N more".
 */
export default function TagCell({
  tags,
  onPickTag,
}: {
  tags: { dimension: string; value: string }[];
  onPickTag?: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const groups = groupTags(tags);
  if (groups.length === 0) return <span className="text-gray-400">untagged</span>;
  const shown = open ? groups : groups.slice(0, TAG_KINDS_SHOWN);
  const hidden = groups.length - shown.length;
  return (
    <span className="flex flex-col gap-1">
      {shown.map(g => (
        <span key={g.dimension} className="flex flex-wrap items-center gap-1">
          <span className="text-[11px] text-gray-400">{`${tagWords(g.dimension)}: `}</span>
          {g.values.map(v => {
            const key = `${g.dimension}:${v}`;
            return (
              <TagChip
                key={key}
                dimension={g.dimension}
                value={v}
                onPick={onPickTag ? () => onPickTag(key) : undefined}
                testId={onPickTag ? `pick-tag-${key}` : undefined}
              />
            );
          })}
        </span>
      ))}
      {(hidden > 0 || open) && groups.length > TAG_KINDS_SHOWN && (
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="self-start text-[11px] text-indigo-600 hover:underline dark:text-indigo-300"
        >
          {open ? 'Show fewer' : `+${hidden} more`}
        </button>
      )}
    </span>
  );
}
